import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useA11y } from '../context/AccessibilityContext';
import { t, LANGUAGES } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Type, Eye, EyeOff, Zap, Languages, Bell, Download,
  Trash2, ChevronRight, CreditCard, Shield, AlertTriangle,
  Check, Monitor, Wrench, RefreshCw, Battery, CheckCircle, XCircle, Tv, Plus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Toggle({ on, onChange, label, id }) {
  return (
    <button
      role="switch"
      aria-checked={on}
      aria-label={label}
      id={id}
      onClick={() => onChange(!on)}
      className={`relative w-11 h-6 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-black ${
        on ? 'bg-violet-600' : 'bg-white/10'
      }`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
        on ? 'translate-x-5' : 'translate-x-0'
      }`} />
    </button>
  );
}

function Section({ title, children }) {
  return (
    <section aria-labelledby={`section-${title}`} className="space-y-1">
      <p id={`section-${title}`}
        className="text-xs font-black tracking-widest text-neutral-500 uppercase px-1 mb-3">
        {title}
      </p>
      <div className="rounded-2xl bg-[#0A0A0A] border border-white/5 overflow-hidden divide-y divide-white/5">
        {children}
      </div>
    </section>
  );
}

function Row({ icon: Icon, label, description, children, onClick, danger = false }) {
  const base = `flex items-center gap-4 px-5 py-4 transition-colors ${
    onClick ? 'cursor-pointer hover:bg-white/3' : ''
  } ${danger ? 'hover:bg-red-500/5' : ''}`;
  return (
    <div className={base} onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? e => e.key === 'Enter' && onClick() : undefined}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
        danger ? 'bg-red-500/10' : 'bg-white/5'
      }`}>
        <Icon className={`w-4 h-4 ${danger ? 'text-red-400' : 'text-neutral-400'}`} />
      </div>
      <div className="flex-1 min-w-0 pr-2">
        <p className={`text-sm font-semibold ${danger ? 'text-red-400' : 'text-white'}`}>{label}</p>
        {description && <p className="text-xs text-neutral-500 mt-0.5 leading-snug">{description}</p>}
      </div>
      <div className="flex-shrink-0 flex items-center justify-end max-w-[60%]">
        {children}
      </div>
    </div>
  );
}

function DetectionHealth() {
  const [isAndroid, setIsAndroid] = useState(false);
  const [permissions, setPermissions] = useState({
    accessibility: false,
    notifications: false,
    battery: false,
    usageStats: false,
  });

  // Detect Android bridge with retries
  useEffect(() => {
    const check = () => {
      if (window.QudeAndroid) setIsAndroid(true);
    };
    check();
    const t1 = setTimeout(check, 500);
    const t2 = setTimeout(check, 1000);
    const t3 = setTimeout(check, 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const checkPermissions = () => {
    if (!window.QudeAndroid) return;
    try {
      setPermissions({
        accessibility: window.QudeAndroid.isAccessibilityEnabled?.() === true,
        notifications: window.QudeAndroid.hasNotificationListenerEnabled?.() === true,
        battery: window.QudeAndroid.needsBatteryOptimizationExemption?.() === false,
        usageStats: window.QudeAndroid.hasUsageStatsPermission?.() === true,
      });
    } catch {}
  };

  useEffect(() => {
    if (!isAndroid) return;
    checkPermissions();
    const interval = setInterval(checkPermissions, 2000);
    const onFocus = () => setTimeout(checkPermissions, 500);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') setTimeout(checkPermissions, 500);
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAndroid]);

  if (!isAndroid) return null;

  const allEnabled = permissions.accessibility && permissions.notifications && permissions.battery && permissions.usageStats;

  const PERMISSION_ROWS = [
    {
      icon: Shield,
      color: '#7c3aed',
      label: 'Auto-Tracking',
      description: 'Reads streaming apps to detect title and episode',
      enabled: permissions.accessibility,
      fixLabel: 'Enable',
      onFix: () => { try { window.QudeAndroid.openAccessibilitySettings?.(); } catch {} },
    },
    {
      icon: Bell,
      color: '#4A9EFF',
      label: 'Notification Access',
      description: 'Detects playback from streaming app alerts',
      enabled: permissions.notifications,
      fixLabel: 'Enable',
      onFix: () => { try { window.QudeAndroid.requestNotificationListenerPermission?.(); } catch {} },
    },
    {
      icon: Battery,
      color: '#10b981',
      label: 'Background Running',
      description: 'Keeps detection active when app is closed',
      enabled: permissions.battery,
      fixLabel: 'Fix',
      onFix: () => { try { window.QudeAndroid.requestBatteryOptimizationExemption?.(); } catch {} },
    },
    {
      icon: Zap,
      color: '#f97316',
      label: 'Usage Access',
      description: 'Fills detection gaps by tracking which streaming apps are open',
      enabled: permissions.usageStats,
      fixLabel: 'Enable',
      onFix: () => { try { window.QudeAndroid.requestUsageStatsPermission?.(); } catch {} },
    },
  ];

  return (
    <Section title="Detection Health">
      {allEnabled ? (
        <div className="px-5 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-400">All systems go</p>
            <p className="text-xs text-neutral-500">Qude is tracking everything automatically</p>
          </div>
        </div>
      ) : (
        <div className="px-5 py-3">
          <p className="text-xs text-amber-400 font-semibold">
            Some permissions are off — detection may be incomplete
          </p>
        </div>
      )}
      {PERMISSION_ROWS.map(row => (
        <div key={row.label} className="flex items-center gap-4 px-5 py-4">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${row.color}15`, border: `1px solid ${row.color}30` }}>
            <row.icon className="w-4 h-4" style={{ color: row.color }} />
          </div>
          <div className="flex-1 min-w-0 pr-2">
            <p className="text-sm font-semibold text-white">{row.label}</p>
            <p className="text-xs text-neutral-500 mt-0.5 leading-snug">{row.description}</p>
          </div>
          <div className="flex-shrink-0">
            {row.enabled ? (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}>
                <CheckCircle className="w-3 h-3 text-emerald-400" />
                <span className="text-xs font-semibold text-emerald-400">On</span>
              </div>
            ) : (
              <button
                onClick={row.onFix}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white"
                style={{ background: `linear-gradient(135deg, ${row.color}, #7c3aed)` }}>
                <XCircle className="w-3 h-3" />
                {row.fixLabel}
              </button>
            )}
          </div>
        </div>
      ))}
    </Section>
  );
}


function ManageTVs() {
  const [isAndroid, setIsAndroid] = useState(false);
  const [tvs, setTvs] = useState([]);
  const [dismissedIps, setDismissedIps] = useState([]);

  useEffect(() => {
    const check = () => { if (window.QudeAndroid) setIsAndroid(true); };
    check();
    const t1 = setTimeout(check, 500);
    const t2 = setTimeout(check, 1000);
    const t3 = setTimeout(check, 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  useEffect(() => {
    if (!isAndroid) return;
    loadTVs();
    const dismissed = JSON.parse(localStorage.getItem('vizio_pairing_dismissed_ips') || '[]');
    setDismissedIps(dismissed);
  }, [isAndroid]);

  const loadTVs = () => {
    try {
      // getAllKnownVizios (not getKnownVizios) — this list is bookkeeping
      // (paired / not paired / hidden), not "is it on right now", so it
      // shouldn't depend on a live network reachability check. That
      // dependency used to mean one bad ping made this whole section
      // disappear until the page was remounted.
      const list = JSON.parse(window.QudeAndroid?.getAllKnownVizios?.() || '[]');
      setTvs(list);
    } catch {}
  };

  const dismissTV = (ip) => {
    const updated = [...dismissedIps, ip];
    setDismissedIps(updated);
    localStorage.setItem('vizio_pairing_dismissed_ips', JSON.stringify(updated));
  };

  const restoreTV = (ip) => {
    const updated = dismissedIps.filter(i => i !== ip);
    setDismissedIps(updated);
    localStorage.setItem('vizio_pairing_dismissed_ips', JSON.stringify(updated));
  };

  if (!isAndroid) return null;

  const pairedTvs = tvs.filter(tv => tv.paired);
  const unpairedTvs = tvs.filter(tv => !tv.paired && !dismissedIps.includes(tv.ip));
  const dismissedTvs = tvs.filter(tv => dismissedIps.includes(tv.ip));

  if (tvs.length === 0 && dismissedTvs.length === 0) return null;

  return (
    <Section title="Manage TVs">
      {pairedTvs.length > 0 && pairedTvs.map(tv => (
        <div key={tv.ip} className="flex items-center gap-4 px-5 py-4">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-emerald-500/10">
            <Tv className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">{tv.name || 'Vizio TV'}</p>
            <p className="text-xs text-neutral-500">{tv.ip} · Paired</p>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}>
            <CheckCircle className="w-3 h-3 text-emerald-400" />
            <span className="text-xs font-semibold text-emerald-400">Connected</span>
          </div>
        </div>
      ))}
      {unpairedTvs.length > 0 && unpairedTvs.map(tv => (
        <div key={tv.ip} className="flex items-center gap-4 px-5 py-4">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-amber-500/10">
            <Tv className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">{tv.name || 'Vizio TV'}</p>
            <p className="text-xs text-neutral-500">{tv.ip} · Not paired</p>
          </div>
          <button onClick={() => dismissTV(tv.ip)}
            className="text-xs text-neutral-500 hover:text-white px-3 py-1.5 rounded-full border border-white/10 transition-colors">
            Hide
          </button>
        </div>
      ))}
      {dismissedTvs.length > 0 && (
        <>
          <div className="px-5 py-2">
            <p className="text-xs text-neutral-600 uppercase tracking-widest font-bold">Hidden TVs</p>
          </div>
          {dismissedTvs.map(tv => (
            <div key={tv.ip} className="flex items-center gap-4 px-5 py-4 opacity-50">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/5">
                <Tv className="w-4 h-4 text-neutral-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">{tv.name || 'Vizio TV'}</p>
                <p className="text-xs text-neutral-500">{tv.ip} · Hidden</p>
              </div>
              <button onClick={() => restoreTV(tv.ip)}
                className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300 px-3 py-1.5 rounded-full border border-violet-500/30 transition-colors">
                <Plus className="w-3 h-3" /> Restore
              </button>
            </div>
          ))}
        </>
      )}
    </Section>
  );
}

function PushRow() {
  const [permission, setPermission] = useState(() => {
    try { return Notification.permission; } catch { return 'unsupported'; }
  });

  const request = async () => {
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
    } catch {}
  };

  if (typeof Notification === 'undefined') return null;

  return (
    <div className="flex items-center gap-4 px-5 py-4">
      <div className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
        <Bell className="w-4 h-4 text-neutral-400" />
      </div>
      <div className="flex-1 min-w-0 pr-2">
        <p className="text-sm font-semibold text-white">Push Notifications</p>
        <p className="text-xs text-neutral-500 mt-0.5">Drop alerts and reminders</p>
      </div>
      <div className="flex-shrink-0">
        {permission === 'granted' ? (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30">
            <CheckCircle className="w-3 h-3 text-emerald-400" />
            <span className="text-xs font-semibold text-emerald-400">Enabled</span>
          </div>
        ) : permission === 'denied' ? (
          <span className="text-xs text-red-400 text-right leading-tight">Blocked in<br/>browser settings</span>
        ) : (
          <button
            onClick={request}
            className="px-3 py-1.5 rounded-full text-xs font-bold text-white"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)' }}
          >
            Enable
          </button>
        )}
      </div>
    </div>
  );
}

const SERVICE_META_SETTINGS = {
  netflix:        { name: 'Netflix',     color: '#E50914' },
  hulu:           { name: 'Hulu',        color: '#1CE783' },
  disney_plus:    { name: 'Disney+',     color: '#113CCF' },
  hbo_max:        { name: 'Max',         color: '#B535F6' },
  amazon_prime:   { name: 'Prime Video', color: '#00A8E1' },
  apple_tv:       { name: 'Apple TV+',   color: '#aaaaaa' },
  peacock:        { name: 'Peacock',     color: '#FDB927' },
  paramount_plus: { name: 'Paramount+',  color: '#0064FF' },
};

function HistoryConsentRow({ getToken }) {
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/detection-consents`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setEnabled(!!res.data?.consents?.service_history);
      } catch {}
      setLoaded(true);
    };
    load();
  }, [getToken]);

  const toggle = async (next) => {
    setEnabled(next); // optimistic — flip back below on failure
    try {
      const token = await getToken();
      await axios.post(`${API}/detection-consents/service_history`, { enabled: next }, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      setEnabled(!next);
      toast.error('Could not save');
    }
  };

  if (!loaded) return null;

  return (
    <Row icon={RefreshCw} label="Watch history sync"
      description="When you visit your watch history on Netflix, Hulu, etc., Qude reads it to fill in shows you watched on other devices.">
      <Toggle on={enabled} onChange={toggle} label="Watch history sync" id="toggle-history-sync" />
    </Row>
  );
}

// Friendly names for detection_health.py's source keys (reconciliation.py's
// SOURCE_CONFIDENCE). Unknown keys fall back to the raw key.
const SOURCE_LABELS = {
  chrome_extension_v1: 'Chrome extension',
  service_history_v1: 'Watch history sync',
  roku_channel: 'Roku',
  roku_ecp_poll: 'Roku (phone)',
  fire_adb: 'Fire TV',
  webos: 'LG TV',
  smartthings: 'Samsung TV',
  cast: 'Chromecast',
  cast_app_only: 'Chromecast (app only)',
  chromecast_mdns_v1: 'Chromecast (home agent)',
  accessibility: 'Android auto-tracking',
  notification_metadata: 'Android notifications',
  ambient_detection: 'Audio detection',
  manual: 'Added by you',
};

// Per-source detection health (GET /api/detection/health): how many signals
// each detector sent in the last 7 days and how many actually landed on the
// Hub, so a detector that quietly stopped working is visible here instead
// of just looking like "nothing was watched."
function DetectionSourcesHealth({ getToken }) {
  const [report, setReport] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/detection/health`, {
          params: { days: 7 },
          headers: { Authorization: `Bearer ${token}` },
        });
        setReport(res.data);
      } catch {}
    };
    load();
  }, [getToken]);

  if (!report) return null;
  const sources = report.sources || [];

  return (
    <div className="px-5 py-4">
      <div className="flex items-center gap-4 mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/5">
          <Monitor className="w-4 h-4 text-neutral-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">Detection sources</p>
          <p className="text-xs text-neutral-500 mt-0.5 leading-snug">
            {sources.length
              ? 'What each device reported in the last 7 days, and how much reached your Hub.'
              : 'No detections in the last 7 days. Play something on a connected device to see it here.'}
          </p>
        </div>
      </div>
      {sources.length > 0 && (
        <ul className="space-y-2">
          {sources.map(s => (
            <li key={s.source} className="rounded-xl bg-white/3 px-3 py-2">
              <div className="flex items-center gap-2">
                {s.status === 'ok'
                  ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                  : <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />}
                <span className="text-sm text-white flex-1 truncate">{SOURCE_LABELS[s.source] || s.source}</span>
                <span className="text-xs text-neutral-400 tabular-nums">
                  {s.written}/{s.total} on Hub
                </span>
              </div>
              {s.flags?.length > 0 && (
                <p className="text-xs text-amber-300/80 mt-1 pl-5">{s.flags.join(' · ')}</p>
              )}
              {s.last_title && (
                <p className="text-xs text-neutral-500 mt-0.5 pl-5 truncate">Last: {s.last_title}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ConnectedServicesSection({ getToken }) {
  const [connected, setConnected] = useState([]);
  const [disconnecting, setDisconnecting] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${API}/services/connected`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setConnected(res.data || []);
      } catch {}
      setLoaded(true);
    };
    load();
  }, [getToken]);

  const disconnect = async (svcId) => {
    setDisconnecting(svcId);
    try {
      const token = await getToken();
      const res = await axios.delete(`${API}/services/disconnect/${svcId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConnected(res.data.connected_services || []);
      toast.success('Disconnected');
    } catch { toast.error('Could not disconnect'); }
    setDisconnecting(null);
  };

  if (!loaded) return (
    <div className="px-5 py-4 flex items-center gap-2 text-neutral-500 text-xs">
      <div className="w-3 h-3 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      Loading services…
    </div>
  );

  if (connected.length === 0) return (
    <div className="px-5 py-4 flex items-center justify-between">
      <p className="text-sm text-neutral-500">No services connected</p>
      <a href="/services" className="text-xs text-violet-400 hover:text-violet-300">Add →</a>
    </div>
  );

  return (
    <>
      {connected.map(svcId => {
        const meta = SERVICE_META_SETTINGS[svcId] || { name: svcId, color: '#666' };
        return (
          <div key={svcId} className="flex items-center gap-4 px-5 py-4">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}30` }}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: meta.color }} />
            </div>
            <p className="text-sm font-semibold text-white flex-1">{meta.name}</p>
            <button
              onClick={() => disconnect(svcId)}
              disabled={disconnecting === svcId}
              className="text-xs text-red-400 hover:text-red-300 px-3 py-1.5 rounded-full border border-red-500/20 hover:border-red-500/40 disabled:opacity-50 transition-colors"
            >
              {disconnecting === svcId ? '…' : 'Disconnect'}
            </button>
          </div>
        );
      })}
      <div className="px-5 py-3 border-t border-white/5">
        <a href="/services" className="text-xs text-violet-400 hover:text-violet-300">+ Add more services</a>
      </div>
    </>
  );
}

export default function Settings() {
  const { user, getToken, signOut } = useAuth();
  const navigate = useNavigate();
  const { settings, update } = useA11y();
  const lang = settings.language;
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [adminSecret, setAdminSecret] = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  const handleBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const token = await getToken();
      const res = await axios.post(`${API}/billing/portal`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.url) window.open(res.data.url, '_blank');
    } catch {
      navigate('/pricing');
    }
    setOpeningPortal(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API}/user/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `qude_export_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Data exported!');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  const handleBackfillDeepLinks = async () => {
    if (!adminSecret) { toast.error('Enter your admin secret first'); return; }
    setBackfilling(true);
    try {
      const res = await axios.post(`${API}/admin/backfill-deep-links`, {},
        { headers: { 'x-admin-secret': adminSecret } });
      toast.success(`Updated ${res.data.updated} shows, skipped ${res.data.skipped} already done`);
    } catch (err) {
      if (err.response?.status === 401) { toast.error('Wrong admin secret'); }
      else { toast.error('Backfill failed'); }
    }
    setBackfilling(false);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) { setDeleteConfirm(true); return; }
    setDeleting(true);
    try {
      const token = await getToken();
      await axios.delete(`${API}/user/account`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Account deleted.');
      if (typeof window !== 'undefined' && window.QudeAndroid?.clearToken) {
        try { window.QudeAndroid.clearToken(); } catch {}
      }
      await signOut();
      navigate('/');
    } catch {
      toast.error('Failed to delete account');
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const TEXT_SIZES = [
    { id: 'sm', label: 'A', className: 'text-xs' },
    { id: 'md', label: 'A', className: 'text-sm' },
    { id: 'lg', label: 'A', className: 'text-base' },
    { id: 'xl', label: 'A', className: 'text-lg' },
  ];

  const COLORBLIND_OPTIONS = [
    { id: 'none',         label: 'Off' },
    { id: 'protanopia',   label: 'Protanopia' },
    { id: 'deuteranopia', label: 'Deuteranopia' },
    { id: 'tritanopia',   label: 'Tritanopia' },
  ];

  return (
    <main className="max-w-xl mx-auto space-y-6 pb-12" aria-label={t('settings_title', lang)}>
      <div>
        <h1 className="text-3xl font-black tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>
          {t('settings_title', lang)}
        </h1>
        {user && (
          <p className="text-neutral-500 text-sm mt-1">
            {user.firstName || user.fullName || user.emailAddresses?.[0]?.emailAddress || ''}
          </p>
        )}
      </div>

      <DetectionHealth />
      <ManageTVs />

      <Section title={t('appearance', lang)}>
        <div className="px-5 py-4">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/5">
              <Type className="w-4 h-4 text-neutral-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">{t('text_size', lang)}</p>
              <p className="text-xs text-neutral-500 mt-0.5">Affects all text across the app</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('text_size', lang)}>
            {TEXT_SIZES.map(size => (
              <button key={size.id} role="radio" aria-checked={settings.textSize === size.id}
                onClick={() => update('textSize', size.id)}
                className={`w-10 h-10 rounded-lg flex items-center justify-center font-black transition-all ${
                  settings.textSize === size.id ? 'bg-violet-600 text-white' : 'bg-white/5 text-neutral-400 hover:text-white'
                } ${size.className}`}>
                {size.label}
              </button>
            ))}
          </div>
        </div>
      </Section>

      <Section title={t('accessibility', lang)}>
        <div className="px-5 py-4">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/5">
              <Eye className="w-4 h-4 text-neutral-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">{t('colorblind_mode', lang)}</p>
              <p className="text-xs text-neutral-500 mt-0.5">Applies a perceptual color filter across the app</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2" role="radiogroup">
            {COLORBLIND_OPTIONS.map(opt => (
              <button key={opt.id} role="radio" aria-checked={settings.colorblind === opt.id}
                onClick={() => update('colorblind', opt.id)}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                  settings.colorblind === opt.id ? 'bg-violet-600 text-white' : 'bg-white/5 text-neutral-400 hover:text-white'
                }`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <Row icon={Zap} label={t('reduce_motion', lang)} description="Disables animations and transitions">
          <Toggle on={settings.reduceMotion} onChange={v => update('reduceMotion', v)}
            label={t('reduce_motion', lang)} id="toggle-motion" />
        </Row>
        <Row icon={Monitor} label={t('rtl_layout', lang)} description="For Arabic and Hebrew users">
          <Toggle on={settings.rtl} onChange={v => update('rtl', v)}
            label={t('rtl_layout', lang)} id="toggle-rtl" />
        </Row>
      </Section>

      <Section title="Viewing">
        <Row icon={EyeOff} label="Spoiler Shield"
          description="Hides episode titles for shows you haven't caught up on">
          <Toggle on={settings.spoilerShield ?? false} onChange={v => update('spoilerShield', v)}
            label="Spoiler Shield" id="toggle-spoiler" />
        </Row>
        <HistoryConsentRow getToken={getToken} />
        <DetectionSourcesHealth getToken={getToken} />
      </Section>

      <Section title={t('language', lang)}>
        <div className="px-5 py-4">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-white/5">
              <Languages className="w-4 h-4 text-neutral-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">{t('language', lang)}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2" role="radiogroup">
            {LANGUAGES.map(l => (
              <button key={l.code} role="radio" aria-checked={settings.language === l.code}
                onClick={() => update('language', l.code)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                  settings.language === l.code ? 'bg-violet-600 text-white' : 'bg-white/5 text-neutral-400 hover:text-white'
                }`}>
                <span>{l.flag}</span>
                <span>{l.label}</span>
                {settings.language === l.code && <Check className="w-3 h-3" />}
              </button>
            ))}
          </div>
        </div>
      </Section>

      <Section title={t('account', lang)}>
        <Row icon={CreditCard} label="Subscription" description="Manage your plan"
          onClick={openingPortal ? undefined : handleBillingPortal}>
          {openingPortal
            ? <div className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            : <ChevronRight className="w-4 h-4 text-neutral-600" />}
        </Row>
        <Row icon={Bell} label={t('notifications', lang)} description="View and manage reminders"
          onClick={() => navigate('/reminders')}>
          <ChevronRight className="w-4 h-4 text-neutral-600" />
        </Row>
        <PushRow />
        <Row icon={Download} label={t('export_data', lang)}
          description="Download your watchlist and history as CSV"
          onClick={exporting ? undefined : handleExport}>
          {exporting
            ? <div className="w-4 h-4 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            : <ChevronRight className="w-4 h-4 text-neutral-600" />}
        </Row>
      </Section>

      <Section title="Connected Services">
        <ConnectedServicesSection getToken={getToken} />
      </Section>

      <Section title="Danger Zone">
        {deleteConfirm ? (
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{t('delete_confirm', lang)}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setDeleteConfirm(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border border-white/10 text-neutral-400 hover:text-white transition-colors">
                {t('cancel', lang)}
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
                {deleting ? t('loading', lang) : 'Yes, delete everything'}
              </button>
            </div>
          </div>
        ) : (
          <Row icon={Trash2} label={t('delete_account', lang)}
            description="Permanently delete your account and all data"
            onClick={handleDelete} danger>
            <ChevronRight className="w-4 h-4 text-red-400/50" />
          </Row>
        )}
      </Section>

      <div className="pt-2">
        {!showAdmin ? (
          <button onClick={() => setShowAdmin(true)}
            className="text-xs text-neutral-600 hover:text-neutral-400 transition-colors">
            Show admin tools
          </button>
        ) : (
          <Section title="Admin">
            <div className="px-5 py-4 space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <Wrench className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-300 mb-1">Admin tools</p>
                  <p className="text-xs text-amber-200/70">Only for Bret.</p>
                </div>
              </div>
              <input type="password" value={adminSecret} onChange={e => setAdminSecret(e.target.value)}
                placeholder="Admin secret (CRON_SECRET)"
                className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-violet-500/50" />
              <button onClick={handleBackfillDeepLinks} disabled={backfilling || !adminSecret}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #4A9EFF)' }}>
                {backfilling ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Refreshing...</> : <><RefreshCw className="w-4 h-4" />Refresh all show links</>}
              </button>
              <button onClick={() => setShowAdmin(false)}
                className="w-full text-xs text-neutral-500 hover:text-neutral-300 transition-colors pt-2">
                Hide admin tools
              </button>
            </div>
          </Section>
        )}
      </div>

      <p className="text-center text-xs text-neutral-700 pb-4">
        Qude · v2.1 · <a href="https://qudetv.com/privacy" className="hover:text-neutral-500">Privacy</a>
        {' · '}
        <a href="https://qudetv.com/terms" className="hover:text-neutral-500">Terms</a>
      </p>
    </main>
  );
}

